import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { search } from 'services/bs-sector-measurable/measurableService';
import { search as searchTarget } from 'services/bs-sector-measurable/targetService';
import { search as searchMeasureabledData } from 'services/bs-sector-measurable/measurableDataService';
import { defaultFormatTime, buildDateList } from 'helpers/kpiHelper';
export const useSectorData = ({
  sector,
  timesAgo,
  selectedCurrentMonth,
  selectedSubOneMonth,
  setIsLoading
}) => {
  const [measurables, setMeasurables] = useState([]);

  const loadData = async () => {
    if (!sector || !timesAgo.length) return;

    setIsLoading(true);

    try {
      const dates = buildDateList(timesAgo);

      const [resMeasurables, resTargets, mesuarableValuesRs] =
        await Promise.all([
          search({ isGetAll: true, sectorId: sector.id }),
          searchTarget({
            sectorId: sector.id,
            dates: [
              dayjs(selectedCurrentMonth).format('YYYY-MM-DD'),
              dayjs(selectedSubOneMonth).format('YYYY-MM-DD')
            ]
          }),
          searchMeasureabledData({
            sectorId: sector.id,
            scheduleType: sector.scheduleType,
            dates
          })
        ]);

      const formulaItems = resMeasurables.items.filter(
        x => x.inputMethod === 3 && x.formula
      );
      let requiredKeys = new Set();
      formulaItems.forEach(item => {
        const keys = extractKeysFromFormula(item.formula);
        keys.forEach(k => requiredKeys.add(k));
      });

      // ✅ group measurable values
      let groupedValues = mesuarableValuesRs?.items?.reduce((acc, item) => {
        if (!acc[item.measurableId]) acc[item.measurableId] = [];
        acc[item.measurableId].push(item);
        return acc;
      }, {});

      const existingIds = new Set(
        Object.keys(groupedValues || {}).map(id => parseInt(id))
      );
      const missingKeys = [...requiredKeys].filter(id => !existingIds.has(id));
      if (missingKeys.length > 0) {
        
        const resExtra = await searchMeasureabledData({
          measurableIds: missingKeys, 
          scheduleType: sector.scheduleType,
          dates
        });

        let extraValues = resExtra?.items || [];
        const allValues = [
          ...(mesuarableValuesRs?.items || []),
          ...extraValues
        ];

        groupedValues = allValues.reduce((acc, item) => {
          if (!acc[item.measurableId]) acc[item.measurableId] = [];
          acc[item.measurableId].push(item);
          return acc;
        }, {});
      }

      const groupedTargets = resTargets?.items?.reduce((acc, item) => {
        if (!acc[item.measurableId]) acc[item.measurableId] = [];
        acc[item.measurableId].push(item);
        return acc;
      }, {});

      const data = resMeasurables.items.map(x => {
        const targetList = groupedTargets?.[x.id] || [];

        const currentMonthTarget = targetList.find(
          t => new Date(t.date).getTime() === selectedCurrentMonth.getTime()
        );

        const previousMonthTarget = targetList.find(
          t => new Date(t.date).getTime() === selectedSubOneMonth.getTime()
        );

        const timeData = timesAgo.reduce((acc, time) => {
          acc[`time${time.index}`] = groupedValues?.[x.id]?.find(
            mv => dayjs(mv.date).format(defaultFormatTime) === time.date
          );
          return acc;
        }, {});

        return {
          ...x,
          dateData: groupedValues?.[x.id] || [],
          ...timeData,
          currentMonthTarget,
          previousMonthTarget
        };
      });

      setMeasurables(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [sector, timesAgo, selectedCurrentMonth, selectedSubOneMonth]);

  return { measurables, reload: loadData };
};

const extractKeysFromFormula = formulaStr => {
  try {
    const formula = JSON.parse(formulaStr)?.formulaWithIds;
    if (!formula) return [];

    const matches = formula.match(/key\d+/g);
    return matches ? matches.map(k => parseInt(k.replace('key', ''))) : [];
  } catch {
    return [];
  }
};
